<template>
  <div class="notif-wrapper" v-click-outside="closePanel">
    <button
      class="notif-bell"
      :class="{ 'notif-bell--active': isPanelOpen && !isMobile, 'notif-bell--no-hover': isMobile }"
      @click="togglePanel"
      aria-label="Notifications"
    >
      <i class="fa fa-bell" style="font-size: 18px;"></i>
      <!-- Unread badge -->
      <span v-if="unreadCount > 0" class="notif-badge" :style=" isMobile ? 'top: -10px; right: -7px;' : '' ">
        {{ unreadCount > 99 ? "99+" : unreadCount }}
      </span>
    </button>
    <span v-if="isMobile" @click="togglePanel" style="padding-left: 10px;">Notifications</span>

    <transition name="notif-panel-slide">
      <div v-if="isPanelOpen" class="notif-panel">
        <!-- Header -->
        <div class="notif-panel__header">
          <h3 class="notif-panel__title">Notifications</h3>
            <label class="notif-toggle">
            <span class="notif-toggle__label">Only show unread</span>
            <div class="notif-toggle__switch" :class="{ 'notif-toggle__switch--on': showUnreadOnly }"
              @click="showUnreadOnly = !showUnreadOnly" role="switch" :aria-checked="showUnreadOnly">
              <div class="notif-toggle__thumb" />
            </div>
          </label>
        </div>
        <div class="notif-panel__divider" />
        <div style="display:flex; justify-content: flex-end; gap:10px; padding: 5px 12px;">
          <button v-if="unreadCount > 0" class="notif-mark-all" @click="markAllNotificationsRead">
            Mark all as read
          </button>
        </div>

        <div v-if="loading" class="notif-panel__center">
          <div class="notif-spinner" />
        </div>

        <div
          v-else-if="displayedNotifications.length === 0"
          class="notif-panel__empty"
        >
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#CBD5E0"
            stroke-width="1.2"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            <line x1="1" y1="1" x2="23" y2="23" stroke="#CBD5E0" />
          </svg>
          <p class="notif-panel__empty-title">
            {{ showUnreadOnly ? "No unread notifications" : "No notifications yet" }}
          </p>
          <p class="notif-panel__empty-sub">
            {{
              showUnreadOnly
                ? "You're all caught up!"
                : ""
            }}
          </p>
        </div>

        <div v-else class="notif-panel__list">
          <div
            v-for="notif in displayedNotifications"
            :key="notif.id"
            class="notif-item"
            :class="{ 'notif-item--unread': !notif.isRead }"
            @click="handleNotifClick(notif)"
          >
            <!-- ── CHANGE 1: icon block extended for all new types ── -->
            <div class="notif-item__icon" :class="`notif-item__icon--${notif.type}`">
              <!-- leave -->
              <i v-if="notif.type === 'leave_applied'" class="fa fa-file-alt"></i>
              <i v-else-if="notif.type === 'leave_approved'" class="fa fa-check-circle"></i>
              <i v-else-if="notif.type === 'leave_rejected'" class="fa fa-times-circle"></i>
              <!-- performance edit -->
              <i v-else-if="notif.type === 'performance_item_added'" class="fa fa-plus-circle"></i>
              <i v-else-if="notif.type === 'performance_item_updated'" class="fa fa-edit"></i>
              <i v-else-if="notif.type === 'performance_score_updated'" class="fa fa-bullseye"></i>
              <i v-else-if="notif.type === 'performance_reopen_updated'" class="fa fa-sync-alt"></i>
              <!-- performance lock -->
              <i v-else-if="notif.type === 'performance_locked'" class="fa fa-lock"></i>
              <i v-else-if="notif.type === 'performance_unlocked'" class="fa fa-lock-open"></i>
              <i v-else-if="notif.type === 'performance_scheduled_lock'" class="fa fa-calendar-check"></i>
              <!-- settings -->
              <i v-else-if="notif.type === 'settings_updated'" class="fa fa-cog"></i>
              <i v-else-if="notif.type === 'general_settings_updated'" class="fa fa-cog"></i>
              <!-- manual hours -->
              <i v-else-if="notif.type === 'hours_added'" class="fa fa-clock"></i>
              <i v-else-if="notif.type === 'hours_updated'" class="fa fa-clock"></i>
              <i v-else-if="notif.type === 'hours_edit_requested'" class="fa fa-clock"></i>
              <i v-else-if="notif.type === 'hours_edit_approved'" class="fa fa-check-circle"></i>
              <i v-else-if="notif.type === 'hours_edit_rejected'" class="fa fa-times-circle"></i>
              <!-- meetings -->
              <i v-else-if="notif.type === 'quarterly_meeting_scheduled'" class="fa fa-calendar-alt"></i>
              <i v-else-if="notif.type === 'quarterly_meeting_deleted'" class="fa fa-calendar-times"></i>
              <i v-else-if="notif.type === 'increment_meeting_scheduled'" class="fa fa-calendar-alt"></i>
              <i v-else-if="notif.type === 'increment_meeting_deleted'" class="fa fa-calendar-times"></i>
              <!-- users -->
              <i v-else-if="notif.type === 'user_created'" class="fa fa-user-plus"></i>
              <i v-else-if="notif.type === 'user_deleted'" class="fa fa-user-minus"></i>
              <!-- payroll -->
              <i v-else-if="notif.type === 'payroll_processed'" class="fa fa-money-bill-wave"></i>
              <i v-else-if="notif.type === 'payroll_draft'" class="fa fa-file-invoice"></i>
              <!-- expenses -->
              <i v-else-if="notif.type === 'expense_added' || notif.type === 'expense_updated'" class="fa fa-receipt"></i>
              <i v-else-if="notif.type === 'expense_deleted'" class="fa fa-trash-alt"></i>
              <!-- fallback -->
              <i v-else class="fa fa-bell"></i>
            </div>
            <!-- ── end CHANGE 1 ── -->

            <div class="notif-item__content">
              <p class="notif-item__title">{{ notif.title }}</p>
              <p class="notif-item__msg">{{ notif.message }}</p>
              <div class="notif-item__meta">
                <span class="notif-item__time">{{ timeAgo(notif.createdAt) }}</span>
              </div>
            </div>

            <div v-if="!notif.isRead" class="notif-item__dot" />
          </div>

          <button
            v-if="hasMore"
            class="notif-show-more"
            :disabled="loadingMore"
            @click="loadMore"
          >
            <div v-if="loadingMore" style="display:flex; justify-content:center;">
              <div class="notif-spinner" />
            </div>
            <span v-else>Show more ({{ remainingCount }} more)</span>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import firebaseLib from "firebase/app";
import {
  listenToNotifications,
  markAsRead,
  showChromeNotification,
  markAllAsRead,
} from "../../../services/notificationService";

const clickOutsideDirective = {
  bind(el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (!el.contains(event.target)) binding.value(event);
    };
    document.addEventListener("mousedown", el.__clickOutsideHandler__);
  },
  unbind(el) {
    document.removeEventListener("mousedown", el.__clickOutsideHandler__);
  },
};

const PAGE_SIZE = 10;

export default {
  name: "NotificationBell",

  directives: {
    "click-outside": clickOutsideDirective,
  },

  props: {
    userId: {
      type: String,
      required: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isPanelOpen: false,
      showUnreadOnly: localStorage.getItem("notif_unread_only") === "true",
      loading: true,
      loadingMore: false,
      allNotifications: [],
      visibleCount: PAGE_SIZE,
      unsubscribe: null,
      seenChromeIds: new Set(),
    };
  },

  computed: {
    filteredNotifications() {
      if (this.showUnreadOnly) {
        return this.allNotifications.filter((n) => !n.isRead);
      }
      return this.allNotifications;
    },

    displayedNotifications() {
      return this.filteredNotifications.slice(0, this.visibleCount);
    },

    unreadCount() {
      return this.allNotifications.filter((n) => !n.isRead).length;
    },

    hasMore() {
      return this.visibleCount < this.filteredNotifications.length;
    },

    remainingCount() {
      return Math.min(
        PAGE_SIZE,
        this.filteredNotifications.length - this.visibleCount
      );
    },
  },

  watch: {
    userId: {
      immediate: true,
      handler(newId) {
        this.teardown();
        if (newId) this.setup(newId);
      },
    },

    showUnreadOnly(val) {
      this.visibleCount = PAGE_SIZE;
      localStorage.setItem("notif_unread_only", val);
    },

    allNotifications(newList, oldList) {
      if (oldList.length === 0) return;
      newList.forEach((notif) => {
        if (!notif.isRead && !this.seenChromeIds.has(notif.id)) {
          this.seenChromeIds.add(notif.id);
          showChromeNotification(notif.title, {
            body: notif.message,
            tag: notif.id,
          });
        }
      });
    },
  },

  beforeDestroy() {
    this.teardown();
  },

  methods: {
    async markAllNotificationsRead() {
      if (!this.userId) return;

      try {
        await markAllAsRead(this.userId);
      } catch (err) {
        console.error("Failed to mark all as read", err);
      }
    },
    setup(userId) {
      this.loading = true;

      const tryListen = (attempts) => {
        try {
          firebaseLib.app();

          this.unsubscribe = listenToNotifications(userId, (notifications) => {
            if (this.loading) {
              notifications.forEach((n) => this.seenChromeIds.add(n.id));
            }
            this.allNotifications = notifications;
            this.loading = false;
          });

        } catch (e) {
          if (attempts > 0) {
            setTimeout(() => tryListen(attempts - 1), 300);
          } else {
            console.error("Firebase failed to initialize after retries.");
            this.loading = false;
          }
        }
      };

      tryListen(10);
    },

    teardown() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      this.allNotifications = [];
      this.loading = true;
    },

    togglePanel() {
      this.isPanelOpen = !this.isPanelOpen;
      if (this.isMobile && this.isPanelOpen) {
        this.$emit('close');
      }
    },

    closePanel() {
      this.isPanelOpen = false;
    },

    async handleNotifClick(notif) {
      if (!notif.isRead) {
        await markAsRead(this.userId, notif.id);
      }
      const target = notif.navigateTo || "/leave";
      if (this.$route && this.$route.fullPath !== target) {
        this.$router.push(target);
      }
      this.closePanel();
    },

    async loadMore() {
      this.loadingMore = true;
      await new Promise((r) => setTimeout(r, 300));
      this.visibleCount += PAGE_SIZE;
      this.loadingMore = false;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    timeAgo(timestamp) {
      if (!timestamp) return "";
      const diff = Date.now() - timestamp;
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      if (days < 7) return `${days}d ago`;
      return new Date(timestamp).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
    },
  },
};
</script>

<style scoped>
.notif-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.notif-bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  transition: background 0.15s, color 0.15s;
}
.notif-bell:hover,
.notif-bell--active {
  background: #f1f5f9;
  color: #0f172a;
}

.notif-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: #ef4444;
  color: #fff;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.notif-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 360px;
  max-width: calc(100vw - 16px);
  max-height: 520px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  z-index: 99999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.notif-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  flex-shrink: 0;
}
.notif-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}
.notif-panel__divider {
  height: 1px;
  background: #e2e8f0;
  flex-shrink: 0;
}

.notif-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  margin-top: 8px;
}
.notif-toggle__label {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}
.notif-toggle__switch {
  width: 32px;
  height: 18px;
  background: #cbd5e0;
  border-radius: 999px;
  position: relative;
  transition: background 0.2s;
  cursor: pointer;
}
.notif-toggle__switch--on {
  background: #3b82f6;
}
.notif-toggle__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}
.notif-toggle__switch--on .notif-toggle__thumb {
  transform: translateX(14px);
}

.notif-panel__center {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}
.notif-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.notif-panel__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  gap: 8px;
}
.notif-panel__empty-title {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin: 4px 0 0;
}
.notif-panel__empty-sub {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

.notif-panel__list {
  overflow-y: auto;
  flex: 1;
}
.notif-panel__list::-webkit-scrollbar {
  width: 4px;
}
.notif-panel__list::-webkit-scrollbar-track {
  background: transparent;
}
.notif-panel__list::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 4px;
}

.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.12s;
  border-bottom: 1px solid #f1f5f9;
  position: relative;
}
.notif-item:last-child {
  border-bottom: none;
}
.notif-item:hover {
  background: #f8fafc;
}
.notif-item--unread {
  background: #eff6ff;
}
.notif-item--unread:hover {
  background: #dbeafe;
}

.notif-item__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;   /* controls icon size */
  flex-shrink: 0;
  background: #f1f5f9;
}

/* ── existing icon colours (untouched) ── */
.notif-item__icon--leave_applied  { background: #fff7ed; }
.notif-item__icon--leave_approved { background: #f0fdf4; }
.notif-item__icon--leave_rejected { background: #fef2f2; }

/* ── CHANGE 2: new icon background colours for each new type ── */
/* performance edit */
.notif-item__icon--performance_item_added    { background: #eff6ff; }
.notif-item__icon--performance_item_updated  { background: #eff6ff; }
.notif-item__icon--performance_score_updated { background: #eff6ff; }
.notif-item__icon--performance_reopen_updated{ background: #eff6ff; }
/* performance lock */
.notif-item__icon--performance_locked          { background: #f5f3ff; }
.notif-item__icon--performance_unlocked        { background: #f5f3ff; }
.notif-item__icon--performance_scheduled_lock  { background: #f5f3ff; }
/* settings */
.notif-item__icon--settings_updated         { background: #f8fafc; }
.notif-item__icon--general_settings_updated { background: #f8fafc; }
/* manual hours */
.notif-item__icon--hours_added          { background: #f0fdfa; }
.notif-item__icon--hours_updated        { background: #f0fdfa; }
.notif-item__icon--hours_edit_requested { background: #fff7ed; }
.notif-item__icon--hours_edit_approved  { background: #f0fdf4; }
.notif-item__icon--hours_edit_rejected  { background: #fef2f2; }
/* meetings */
.notif-item__icon--quarterly_meeting_scheduled { background: #f0fdf4; }
.notif-item__icon--quarterly_meeting_deleted   { background: #fef2f2; }
.notif-item__icon--increment_meeting_scheduled { background: #f0fdf4; }
.notif-item__icon--increment_meeting_deleted   { background: #fef2f2; }
/* users */
.notif-item__icon--user_created { background: #eef2ff; }
.notif-item__icon--user_deleted { background: #fef2f2; }
/* payroll */
.notif-item__icon--payroll_processed { background: #fefce8; }
.notif-item__icon--payroll_draft     { background: #fefce8; }
/* ── end CHANGE 2 ── */

.notif-item__content {
  flex: 1;
  min-width: 0;
}
.notif-item__title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 2px;
  white-space: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.notif-item__msg {
  font-size: 12px;
  color: #475569;
  margin: 0 0 4px;
  line-height: 1.4;
  display: -webkit-box;
  /* -webkit-line-clamp: 2; */
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.notif-item__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.notif-item__date {
  font-size: 11px;
  color: #3b82f6;
  font-weight: 500;
}
.notif-item__time {
  font-size: 11px;
  color: #94a3b8;
}

.notif-item__dot {
  width: 8px;
  height: 8px;
  background: #3b82f6;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
}

.notif-show-more {
  width: 100%;
  padding: 12px;
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border-top: 1px solid #e2e8f0;
  transition: background 0.12s;
}
.notif-show-more:hover {
  background: #f1f5f9;
}
.notif-show-more:disabled {
  color: #94a3b8;
  cursor: default;
}

.notif-panel-slide-enter-active,
.notif-panel-slide-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.notif-panel-slide-enter,
.notif-panel-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}
.notif-mark-all {
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.12s;
}

.notif-mark-all:hover {
  background: #f1f5f9;
}

.notif-bell--no-hover {
  width: 16px;
  height: 16px;
}
.notif-bell--no-hover:hover {
  background: transparent;
  color: #64748b;
}

@media (max-width: 990px) {
  .notif-panel {
    position: fixed;
    top: 52px;
    right: 16px;
    max-width: none;
    border-radius: 12px;
    z-index: 999999;
  }
}

@media (max-width: 640px) {
  .notif-panel {
      /* left: 8px; */
      width: 90%;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/Dashboard/Layout/NotificationBell.vue